/**
 * Category Manager - Main Page Component
 * 
 * Displays a list of product categories with CRUD operations.
 * Categories affect FC calculations, shipping constraints, and product pricing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Spinner, Alert, Table, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import CategoryEditorModal from './CategoryEditorModal';

const FUNCTIONS_URL = process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

export default function CategoryManager() {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditor, setShowEditor] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [seeding, setSeeding] = useState(false);

    // Fetch categories from backend
    const fetchCategories = useCallback(async (refresh = false) => {
        try {
            setLoading(true);
            setError(null);

            const url = `${FUNCTIONS_URL}/categoryList${refresh ? '?refresh=true' : ''}`;
            const response = await fetch(url);
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to fetch categories');
            }

            setCategories(data.categories || []);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setError(err.message);
            toast.error('Failed to load categories');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Seed default categories
    const handleSeedCategories = async () => {
        try {
            setSeeding(true);
            const response = await fetch(`${FUNCTIONS_URL}/categorySeed`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to seed categories');
            }

            toast.success('Default categories created!');
            await fetchCategories(true);
        } catch (err) {
            console.error('Error seeding categories:', err);
            toast.error(err.message);
        } finally {
            setSeeding(false);
        }
    };

    // Open editor for new category
    const handleCreateNew = () => {
        setEditingCategory(null);
        setShowEditor(true);
    };

    // Open editor for existing category
    const handleEdit = (category) => {
        setEditingCategory(category);
        setShowEditor(true);
    };

    // Delete a category
    const handleDelete = async (category) => {
        if (category.isSystem) {
            toast.warning('Cannot delete system categories');
            return;
        }

        if (!window.confirm(`Delete category "${category.name}"? This cannot be undone.`)) {
            return;
        }

        try {
            const response = await fetch(`${FUNCTIONS_URL}/categoryDelete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: category.id })
            });
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to delete category');
            }

            toast.success('Category deleted');
            await fetchCategories(true);
        } catch (err) {
            console.error('Error deleting category:', err);
            toast.error(err.message);
        }
    };

    // Handle save from editor modal
    const handleSave = async (categoryData) => {
        try {
            const isNew = !editingCategory;
            const url = isNew ? `${FUNCTIONS_URL}/categoryCreate` : `${FUNCTIONS_URL}/categoryUpdate`;

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(isNew ? categoryData : { id: editingCategory.id, ...categoryData })
            });
            const data = await response.json();

            if (!data.success) {
                throw new Error(data.error || 'Failed to save category');
            }

            toast.success(isNew ? 'Category created!' : 'Category updated!');
            setShowEditor(false);
            await fetchCategories(true);
        } catch (err) {
            console.error('Error saving category:', err);
            toast.error(err.message);
            throw err; // Re-throw so modal can handle it
        }
    };

    // Format currency
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(value || 0);
    };

    // Get seasonal badge text
    const getSeasonalText = (seasonal) => {
        if (!seasonal?.enabled) return null;
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${months[seasonal.startMonth - 1]} - ${months[seasonal.endMonth - 1]}`;
    };

    return (
        <Container fluid className="py-4">
            {/* Header */}
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h2 className="mb-1">
                                <FontAwesomeIcon icon="tags" className="me-2 text-primary" />
                                Product Categories
                            </h2>
                            <p className="text-muted mb-0">
                                Manage categories for FC calculations, shipping rules, and product handling
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button variant="outline-secondary" onClick={() => fetchCategories(true)} disabled={loading}>
                                <FontAwesomeIcon icon="sync" spin={loading} className="me-1" />
                                Refresh
                            </Button>
                            <Button variant="primary" onClick={handleCreateNew}>
                                <FontAwesomeIcon icon="plus" className="me-1" />
                                New Category
                            </Button>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError(null)}>
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                    {error}
                </Alert>
            )}

            {/* Empty State */}
            {!loading && categories.length === 0 && (
                <Card className="text-center py-5">
                    <Card.Body>
                        <FontAwesomeIcon icon="folder-open" size="3x" className="text-muted mb-3" />
                        <h4>No Categories Found</h4>
                        <p className="text-muted mb-4">
                            Get started by seeding the default categories or creating a new one.
                        </p>
                        <div className="d-flex gap-2 justify-content-center">
                            <Button variant="outline-primary" onClick={handleSeedCategories} disabled={seeding}>
                                {seeding ? <Spinner size="sm" className="me-1" /> : null}
                                Seed Default Categories
                            </Button>
                            <Button variant="primary" onClick={handleCreateNew}>
                                <FontAwesomeIcon icon="plus" className="me-1" />
                                Create New
                            </Button>
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Loading State */}
            {loading && categories.length === 0 && (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-muted mt-2">Loading categories...</p>
                </div>
            )}

            {/* Category Cards */}
            {categories.length > 0 && (
                <Row className="g-4">
                    {categories.map((category) => (
                        <Col key={category.id} xs={12} md={6} lg={4}>
                            <Card className="h-100 shadow-sm">
                                <Card.Header className="d-flex align-items-center justify-content-between py-3">
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="rounded-circle p-2 me-3"
                                            style={{ backgroundColor: category.color || '#6c757d', width: '40px', height: '40px' }}
                                        >
                                            <FontAwesomeIcon
                                                icon={category.icon || 'tag'}
                                                className="text-white"
                                                style={{ width: '100%', height: '100%' }}
                                            />
                                        </div>
                                        <div>
                                            <h5 className="mb-0">{category.name}</h5>
                                            <small className="text-muted">{category.slug}</small>
                                        </div>
                                    </div>
                                    <Dropdown align="end">
                                        <Dropdown.Toggle variant="link" className="text-muted p-0">
                                            <FontAwesomeIcon icon="ellipsis-v" />
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu>
                                            <Dropdown.Item onClick={() => handleEdit(category)}>
                                                <FontAwesomeIcon icon="edit" className="me-2" />
                                                Edit
                                            </Dropdown.Item>
                                            <Dropdown.Divider />
                                            <Dropdown.Item
                                                onClick={() => handleDelete(category)}
                                                disabled={category.isSystem}
                                                className={category.isSystem ? 'text-muted' : 'text-danger'}
                                            >
                                                <FontAwesomeIcon icon="trash" className="me-2" />
                                                Delete
                                            </Dropdown.Item>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                </Card.Header>
                                <Card.Body>
                                    {/* Cost Info */}
                                    <div className="mb-3">
                                        <small className="text-muted d-block mb-1">Material Cost</small>
                                        <h4 className="mb-0">{formatCurrency(category.materialCost)}</h4>
                                        {category.handlingCost > 0 && (
                                            <small className="text-muted">+ {formatCurrency(category.handlingCost)} handling</small>
                                        )}
                                    </div>

                                    {/* Badges */}
                                    <div className="d-flex flex-wrap gap-2 mb-3">
                                        {category.isSystem && (
                                            <Badge bg="secondary">System</Badge>
                                        )}
                                        {category.seasonal?.enabled && (
                                            <Badge bg="info">
                                                <FontAwesomeIcon icon="calendar" className="me-1" />
                                                Seasonal: {getSeasonalText(category.seasonal)}
                                            </Badge>
                                        )}
                                        {category.shippingConstraints?.minServiceLevel && (
                                            <Badge bg="warning" text="dark">
                                                <FontAwesomeIcon icon="shipping-fast" className="me-1" />
                                                Min: {category.shippingConstraints.minServiceLevel}
                                            </Badge>
                                        )}
                                    </div>

                                    {/* Tag Rules */}
                                    {category.tagRules?.length > 0 && (
                                        <div>
                                            <small className="text-muted d-block mb-1">Tag Rules</small>
                                            <div className="d-flex flex-wrap gap-1">
                                                {category.tagRules.map((rule, idx) => (
                                                    <Badge key={idx} bg="light" text="dark" className="border">
                                                        {rule.tagName || `ID: ${rule.tagId}`}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Editor Modal */}
            <CategoryEditorModal
                show={showEditor}
                onHide={() => setShowEditor(false)}
                category={editingCategory}
                onSave={handleSave}
            />
        </Container>
    );
}
